import React, { useRef, useState, useEffect, useMemo, useCallback } from 'react';
import { I18n } from 'aws-amplify';
import {
  fetchAccount,
  fetchAccountUsers,
  updateAccountDisplayName,
  updateAccountUser,
} from '../../components/util/callApi.js';
import { ACCOUNT_USER_TYPE, ADMIN_USER_TYPES } from '../../configures/val.js';
import Dialog from '../../components/common/Dialog/index.js';
import Input from '../../components/common/Input/index.js';
import SelectBox from '../../components/common/SelectBox/index.js';
import Button from '../../components/common/Button/index.js';
import styles from './styles.module.css';

/**
 * アカウント管理画面
 * @param {Object} props
 * @param {function} props.updateHeader - ヘッダの更新メソッド
 * @param {number|undefined} props.accountId - アカウント ID
 * @param {number|undefined} props.userId - ユーザ ID
 * @returns {JSX.Element}
 */
const AccountManagement = ({ updateHeader, accountId, userId }) => {
  const updateHeaderRef = useRef(updateHeader);
  const [account, setAccount] = useState(null);
  const [admins, setAdmins] = useState([]);
  const [newDisplayName, setNewDisplayName] = useState('');
  const [newPrimaryAdminId, setNewPrimaryAdminId] = useState(null);
  const [isSavingComplate, setIsSavingComplate] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [dialogContent, setDialogContent] = useState(null);

  /**
   * 現在のアカウント表示名
   */
  const currentDisplayName = useMemo(() => {
    return account?.displayName ?? '';
  }, [account]);

  /**
   * 現在の管理者代表ユーザの id
   */
  const currentPrimaryAdminId = useMemo(() => {
    return admins.find(({ type }) => type === ACCOUNT_USER_TYPE.PRIMARY_ADMIN)?.userId;
  }, [admins]);

  /**
   * 自身が管理者代表かどうか
   */
  const amIPrimaryAdmin = useMemo(() => {
    return currentPrimaryAdminId === userId;
  }, [currentPrimaryAdminId, userId]);

  /**
   * 有効な表示名かどうか
   */
  const isValidDisplayName = useMemo(() => {
    const maxChars = 50;
    return newDisplayName.length <= maxChars;
  }, [newDisplayName]);

  /**
   * 管理者代表の選択肢
   */
  const primaryAdminSelectOptions = useMemo(() => {
    return admins.map(({ userId: adminUserId, familyName, givenName, mailAddress }) => {
      return {
        value: adminUserId,
        display: `${familyName}${givenName} ${mailAddress}`,
      };
    });
  }, [admins]);

  /**
   * アカウント情報を取得して状態変数にセットする
   */
  const loadAccount = useCallback(async () => {
    if (typeof accountId !== 'number') return;

    try {
      const { ok, data } = await fetchAccount(accountId);
      if (ok) {
        setAccount(data);
      }
    } catch (error) {
      console.error('Failed to fetch account data', error);
    }
  }, [accountId]);

  /**
   * アカウントユーザ情報を取得して状態変数にセットする
   */
  const loadAccountUsers = useCallback(async () => {
    if (typeof accountId !== 'number') return;

    try {
      const { ok, data } = await fetchAccountUsers(accountId);
      if (ok) setAdmins(data.users.filter(({ type }) => ADMIN_USER_TYPES.includes(type)));
    } catch (error) {
      console.error('Failed to fetch account users data', error);
    }
  }, [accountId]);

  // ヘッダを更新する
  useEffect(() => {
    if (updateHeaderRef.current) updateHeaderRef.current();
  }, []);

  useEffect(() => {
    loadAccount();
  }, [loadAccount]);

  useEffect(() => {
    loadAccountUsers();
  }, [loadAccountUsers]);

  useEffect(() => {
    setNewDisplayName(currentDisplayName);
  }, [currentDisplayName]);

  useEffect(() => {
    if (!admins.length) return;
    setNewPrimaryAdminId(
      admins.find(({ type }) => type === ACCOUNT_USER_TYPE.PRIMARY_ADMIN)?.userId
    );
  }, [admins]);

  // ダイアログを閉じた後にデータを再取得する
  useEffect(() => {
    if (isDialogOpen) return;
    loadAccount();
    loadAccountUsers();
  }, [isDialogOpen, loadAccount, loadAccountUsers]);

  /**
   * 表示名および管理者代表の変更を保存する。
   * 保存に成功すればメッセージの表示フラグを、失敗すればダイアログ表示フラグをそれぞれ true にする。
   */
  const save = useCallback(async () => {
    let content = I18n.get('Failed to save changes.');
    /** 管理者代表に設定しようとしたユーザが管理者でなかった場合に返されるエラーメッセージ */
    const notPermittedErrMsg = "Change is not permitted.";
    try {
      if (currentDisplayName !== newDisplayName) {
        const { ok } = await updateAccountDisplayName(accountId, newDisplayName);
        if (!ok) throw new Error(content);
      }
      if (amIPrimaryAdmin && currentPrimaryAdminId !== newPrimaryAdminId) {
        const { ok, data } = await updateAccountUser(accountId, newPrimaryAdminId, {
          type: ACCOUNT_USER_TYPE.PRIMARY_ADMIN,
        });
        if (!ok) {
          if (data?.message === notPermittedErrMsg)
            content += I18n.get('The admin (primary) must be a user with admin privileges.');
          throw new Error(content);
        }
      }
      setIsSavingComplate(true);
    } catch (e) {
      setDialogContent(e.message);
      setIsDialogOpen(true);
    }
  }, [
    accountId,
    amIPrimaryAdmin,
    currentDisplayName,
    newDisplayName,
    currentPrimaryAdminId,
    newPrimaryAdminId,
  ]);

  if (!(account && admins && currentPrimaryAdminId && newPrimaryAdminId)) {
    console.log(account, admins, currentPrimaryAdminId, newPrimaryAdminId)
    return <>{I18n.get('Now loading...')}</>;
  }

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>{I18n.get('Account Info')}</h1>
      <dl className={styles.accountInfo}>
        <div>
          <dt>{I18n.get('Account ID')}</dt>
          <dd className={styles.dataValue}>{account.key}</dd>
        </div>
        <div>
          <dt>{I18n.get('Account Name')}</dt>
          <dd className={styles.dataValue}>{account.name}</dd>
        </div>
        <div>
          <dt>{I18n.get('Account Name (display)')}</dt>
          <dd className={styles.dataValue}>
            <Input
              name='display-name-input'
              placeholder={account?.name}
              value={newDisplayName}
              onChange={(e) => setNewDisplayName(e.target.value)}
              scale='max'
            />
          </dd>
          <dd className={`${styles.supplement} ${!isValidDisplayName ? styles.error : ''}`}>
            {I18n.get('Account name (display) must be within 50 characters.')}
          </dd>
        </div>
        <div>
          <dt>{I18n.get('Admin (primary)')}</dt>
          <dd className={styles.dataValue}>
            <SelectBox
              name='primary-admin-select'
              options={primaryAdminSelectOptions}
              value={newPrimaryAdminId}
              onChange={(e) => setNewPrimaryAdminId(Number(e.target.value))}
              scale='max'
              disabled={!amIPrimaryAdmin}
            />
          </dd>
          <dd className={styles.supplement}>
            {I18n.get('* Only users set as administrators can be selected.')}
          </dd>
        </div>
      </dl>
      <div className={styles.saveArea}>
        <p hidden={!isSavingComplate}>{I18n.get('Saved changes.')}</p>
        <Button
          color='rich'
          text={I18n.get('Save')}
          size='large'
          onClick={save}
          disabled={!isValidDisplayName}
        />
      </div>
      <Dialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        content={dialogContent}
        type='alert'
      />
    </div>
  );
};

export default AccountManagement;
